/**
 * Toast Notifications Module
 * Handles toast notification display and management with type safety
 */


type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastOptions {
    title?: string;
    autoHide?: boolean;
    duration?: number;
}

/**
 * ToastNotifications class for managing toast messages
 * Provides type-safe toast notification system with auto-hide and queue management
 */
export class ToastNotifications {
    private container: HTMLElement | null;
    private toasts: Map<string, HTMLElement>;
    private autoHideDelay: number;
    private maxToasts: number;

    constructor() {
        this.container = null;
        this.toasts = new Map<string, HTMLElement>();
        this.autoHideDelay = 5000; // 5 seconds
        this.maxToasts = 5;
        this.initializeContainer();
    }

    /**
     * Initialize the toast container in the DOM
     */
    private initializeContainer(): void {
        this.container = document.getElementById('toast-container');

        if (!this.container) {
            this.createContainer();
        }
    }

    /**
     * Create the toast container element
     */
    private createContainer(): void {
        this.container = document.createElement('div');
        this.container.id = 'toast-container';
        this.container.className = 'fixed top-4 right-4 z-50 space-y-2';
        document.body.appendChild(this.container);
    }

    /**
     * Show a toast notification
     * @param message - The message to display
     * @param type - The type of toast (success, error, warning, info)
     * @param options - Additional options for the toast
     * @returns The toast ID
     */
    public show(message: string, type: ToastType = 'info', options: ToastOptions = {}): string {
        const toastId = this.generateToastId();
        const toast = this.createToast(toastId, message, type, options);

        this.addToast(toastId, toast);
        this.scheduleAutoHide(toastId, options.autoHide !== false);

        return toastId;
    }

    /**
     * Generate a unique toast ID
     */
    private generateToastId(): string {
        return 'toast-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
    }

    /**
     * Create a toast element
     */
    private createToast(id: string, message: string, type: ToastType, options: ToastOptions): HTMLElement {
        const toast = document.createElement('div');
        toast.id = id;
        toast.className = this.getToastClasses(type);

        const icon = this.getIcon(type);
        const title = options.title || this.getDefaultTitle(type);

        toast.innerHTML = `
            <div class="flex items-start">
                <div class="flex-shrink-0">
                    ${icon}
                </div>
                <div class="ml-3 flex-1" style="min-width: 250px;">
                    ${title ? `
                        <p class="text-sm font-medium ${this.getTitleColor(type)}">
                            ${this.escapeHtml(title)}
                        </p>
                    ` : ''}
                    <p class="text-sm ${this.getMessageColor(type)} break-words">
                        ${this.escapeHtml(message)}
                    </p>
                </div>
                <div class="ml-4 flex-shrink-0 flex">
                    <button class="toast-close bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            onclick="window.toastNotifications?.hide('${id}')">
                        <span class="sr-only">Close</span>
                        <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                        </svg>
                    </button>
                </div>
            </div>
        `;

        // Add animation classes
        toast.style.transform = 'translateX(100%)';
        toast.style.opacity = '0';

        return toast;
    }

    /**
     * Get CSS classes for toast based on type
     */
    private getToastClasses(type: ToastType): string {
        const baseClasses = 'max-w-md w-full shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-visible transition-all duration-300 ease-in-out';

        const typeClasses: Record<ToastType, string> = {
            success: 'bg-green-50 ring-green-200',
            error: 'bg-red-50 ring-red-200',
            warning: 'bg-yellow-50 ring-yellow-200',
            info: 'bg-blue-50 ring-blue-200'
        };

        return `${baseClasses} ${typeClasses[type] || typeClasses.info} p-4`;
    }

    /**
     * Get icon SVG for toast type
     */
    private getIcon(type: ToastType): string {
        const icons: Record<ToastType, string> = {
            success: `
                <svg class="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            `,
            error: `
                <svg class="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            `,
            warning: `
                <svg class="h-6 w-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
            `,
            info: `
                <svg class="h-6 w-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            `
        };

        return icons[type] || icons.info;
    }

    /**
     * Get default title for toast type
     */
    private getDefaultTitle(type: ToastType): string {
        const titles: Record<ToastType, string> = {
            success: 'Success',
            error: 'Error',
            warning: 'Warning',
            info: 'Information'
        };

        return titles[type] || '';
    }

    /**
     * Get title text color class for toast type
     */
    private getTitleColor(type: ToastType): string {
        const colors: Record<ToastType, string> = {
            success: 'text-green-800',
            error: 'text-red-800',
            warning: 'text-yellow-800',
            info: 'text-blue-800'
        };

        return colors[type] || colors.info;
    }

    /**
     * Get message text color class for toast type
     */
    private getMessageColor(type: ToastType): string {
        const colors: Record<ToastType, string> = {
            success: 'text-green-700',
            error: 'text-red-700',
            warning: 'text-yellow-700',
            info: 'text-blue-700'
        };

        return colors[type] || colors.info;
    }

    /**
     * Add toast to container with animation
     */
    private addToast(id: string, toast: HTMLElement): void {
        // Remove excess toasts if at limit
        if (this.toasts.size >= this.maxToasts) {
            const oldestId = Array.from(this.toasts.keys())[0];
            this.hide(oldestId);
        }

        // Add toast to container
        if (this.container) {
            this.container.appendChild(toast);
            this.toasts.set(id, toast);

            // Trigger animation
            requestAnimationFrame(() => {
                toast.style.transform = 'translateX(0)';
                toast.style.opacity = '1';
            });

            console.log(`Toast shown: ${id}`);
        }
    }

    /**
     * Hide a toast by ID
     */
    public hide(id: string): void {
        const toast = this.toasts.get(id);
        if (!toast) return;

        // Animate out
        toast.style.transform = 'translateX(100%)';
        toast.style.opacity = '0';

        // Remove after animation
        setTimeout(() => {
            if (toast.parentNode) {
                toast.parentNode.removeChild(toast);
            }
            this.toasts.delete(id);
        }, 300);

        console.log(`Toast hidden: ${id}`);
    }

    /**
     * Schedule auto-hide for a toast
     */
    private scheduleAutoHide(id: string, shouldAutoHide: boolean = true): void {
        if (!shouldAutoHide) return;

        setTimeout(() => {
            this.hide(id);
        }, this.autoHideDelay);
    }

    /**
     * Hide all toasts
     */
    public hideAll(): void {
        Array.from(this.toasts.keys()).forEach(id => {
            this.hide(id);
        });
    }

    /**
     * Escape HTML to prevent XSS
     */
    private escapeHtml(text: string): string {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    // Convenience methods

    /**
     * Show success toast
     */
    public success(message: string, options: ToastOptions = {}): string {
        return this.show(message, 'success', options);
    }

    /**
     * Show error toast (does not auto-hide by default)
     */
    public error(message: string, options: ToastOptions = {}): string {
        return this.show(message, 'error', { ...options, autoHide: false });
    }

    /**
     * Show warning toast
     */
    public warning(message: string, options: ToastOptions = {}): string {
        return this.show(message, 'warning', options);
    }

    /**
     * Show info toast
     */
    public info(message: string, options: ToastOptions = {}): string {
        return this.show(message, 'info', options);
    }

    // Discovery-specific notifications

    /**
     * Show discovery started notification
     */
    public discoveryStarted(target: string): string {
        return this.info(`Discovery started for ${target}`, {
            title: 'Discovery Started'
        });
    }

    /**
     * Show discovery completed notification
     */
    public discoveryCompleted(target: string, devicesFound: number): string {
        return this.success(
            `Discovery completed for ${target}. Found ${devicesFound} device(s).`,
            { title: 'Discovery Completed' }
        );
    }

    /**
     * Show discovery failed notification
     */
    public discoveryFailed(target: string, error: string): string {
        return this.error(
            `Discovery failed for ${target}: ${error}`,
            { title: 'Discovery Failed' }
        );
    }

    /**
     * Show device discovered notification
     */
    public deviceDiscovered(deviceId: string, hostname?: string): string {
        return this.success(
            hostname ? `${deviceId} (${hostname})` : deviceId,
            { title: 'Device Discovered' }
        );
    }

    /**
     * Show credential generated notification
     */
    public credentialGenerated(credentialType: string, target: string): string {
        return this.success(
            `${credentialType} credential generated for ${target}`,
            { title: 'Credential Generated' }
        );
    }

    /**
     * Show credential expired notification
     */
    public credentialExpired(credentialType: string, target: string): string {
        return this.warning(
            `${credentialType} credential for ${target} has expired`,
            { title: 'Credential Expired' }
        );
    }

    /**
     * Show configuration saved notification
     */
    public configurationSaved(): string {
        return this.success('Configuration saved successfully', {
            title: 'Settings Saved'
        });
    }

    /**
     * Show configuration error notification
     */
    public configurationError(error: string): string {
        return this.error(
            `Failed to save configuration: ${error}`,
            { title: 'Configuration Error' }
        );
    }

    // Settings methods

    /**
     * Set maximum number of toasts to display
     */
    public setMaxToasts(max: number): void {
        this.maxToasts = max;
    }

    /**
     * Set auto-hide delay in milliseconds
     */
    public setAutoHideDelay(delay: number): void {
        this.autoHideDelay = delay;
    }

    /**
     * Get current number of active toasts
     */
    public getToastCount(): number {
        return this.toasts.size;
    }

    /**
     * Check if a toast exists
     */
    public hasToast(id: string): boolean {
        return this.toasts.has(id);
    }
}

// Create global instance
declare global {
    interface Window {
        ToastNotifications: typeof ToastNotifications;
        toastNotifications: ToastNotifications;
        showToastNotification: (message: string, type?: ToastType, options?: ToastOptions) => string;
        showToastNotificationNotification: (message: string, type?: ToastType) => string;
    }
}

window.ToastNotifications = ToastNotifications;

// Initialize global instance for immediate use
window.toastNotifications = new ToastNotifications();

// Backward compatibility functions
window.showToastNotification = function(message: string, type: ToastType = 'info', options: ToastOptions = {}): string {
    return window.toastNotifications.show(message, type, options);
};

window.showToastNotificationNotification = function(message: string, type: ToastType = 'info'): string {
    return window.toastNotifications.show(message, type);
};

